import { VideoExtensions } from '../Utils/Constants';
import Queue from '../Utils/Queue';
/**
 * @class
 * @description This class stores information about every CDN available for the played content.
 * It also keeps updated the estimated bandwidth value.
 * @exports Cdn
 */
export default class Cdn {
    /**
     * Constructs Cdn definition class.
     * @param {string} provider CDN provider.
     * @param {string} name CDN name.
     * @param {String} url CDN URL (host and path).
     * @param {number} score Given score by the API.
     * @param {number} relScore Relative score calculated with other CDN scores.
     * @param {boolean} original If the CDN is the one used in the manifest URL provided by the customer.
     */
    constructor(provider, name, url, score, relScore, original) {
        this.downloadedChunks = 0;
        this.downloadedBytes = 0;
        this.downloadedBytesForScoring = 0; // doesn't include little chunks
        this.downloadTimeForScoring = 0; // doesn't include little chunks
        this.hugeChunks = 0;
        this.firstHugeChunkSize = 0;
        this.bandwidthForScoring = new Queue(20); // doesn't include little chunks
        this.wBandwidthForScoring = new Queue(20); // doesn't include little chunks
        this.failures = 0;
        this.retries = 0;
        this.totalFail = 0;
        this.cacheHit = 0;
        this.downloadMillis = 0;
        this.usedOnce = false;
        this.maxBandwidth = 1000000000000;
        this.lastPreferedVideoBandwidth = 0;
        this._lastsPreferedVideoBandwidth = new Array();
        this.highestRenditionBandwidth = 0;
        this.lastPreferedVideoChunkSize = 0;
        this.lastPreferedVideoChunkDownloadTime = 0;
        this.lastPingTime = 0;
        this._accumBw = Cdn.initialBw;
        this.bandwidth = new Queue(20);
        this.wBandwidth = new Queue(20);
        this._lastResponses = [];
        this.downloadedSegments = new Array();
        this.provider = provider;
        this.name = name;
        this.url = url;
        this.score = score;
        this.relScore = relScore;
        this.original = original;
        this.active = true;
        this.banned = false;
    }
    pushLastPreferedVideoBandwidth(bandwidth) {
        this._lastsPreferedVideoBandwidth.push(bandwidth);
        if (this._lastsPreferedVideoBandwidth.length > 5) {
            this._lastsPreferedVideoBandwidth.shift();
        }
    }
    getAvgLastVideoBandwidth() {
        let result = 0;
        var i = 0;
        for (i = 0; i < this._lastsPreferedVideoBandwidth.length; i++) {
            result = result + this._lastsPreferedVideoBandwidth[i];
        }
        return (result / i);
    }
    /**
     * Add the data of a response, successful or not, of the defined CDN.
     * It stores the properties of the response in lastResponses list and calculates the future estimated bandwidth.
     * @param {VideoSegment} segment URL of the request.
     * @public
     */
    addResp(segment) {
        var _a, _b;
        let route = segment.route;
        let bandwidth = segment.bandwidth;
        let size = segment.size || 0;
        let time = segment.downloadTimeMs;
        let failed = segment.failed;
        let retries = failed ? true : false;
        this.downloadedChunks++;
        this.downloadedBytes += size || 0;
        if (segment.rendition != null)
            if (segment.rendition.isAudio && this.hugeChunks == 0) {
                this.usedOnce = false;
            }
        if (segment.rendition == null || !segment.rendition.isAudio) {
            this.downloadedBytesForScoring += size || 0;
            this.downloadTimeForScoring += time || 0;
            this.usedOnce = true;
            this.hugeChunks++;
            if (this.firstHugeChunkSize == 0) {
                this.firstHugeChunkSize = size;
            }
        }
        if (retries) {
            this.retries++;
        }
        // Bandwidth for video segments only
        VideoExtensions.forEach((val) => {
            if (route.indexOf(val) >= 0) {
                if (bandwidth > 0) {
                    this._accumBw = this._accumBw !== Cdn.initialBw ? Math.trunc((this._accumBw + bandwidth) / 2) : bandwidth;
                    this.bandwidth.add(bandwidth);
                    this.wBandwidth.add({ bw: bandwidth * size, ct: size });
                    if (segment.rendition == null || !segment.rendition.isAudio) {
                        this.bandwidthForScoring.add(bandwidth);
                        this.wBandwidthForScoring.add({ bw: bandwidth * size, ct: size });
                    }
                }
                else {
                    this.cacheHit++;
                }
                // if there is a hit to the cache
                if (this._accumBw == 0) {
                    this.usedOnce = false;
                }
            }
        });
        if (this.downloadedBytes == 0) {
            this.usedOnce = false;
        }
        this.downloadMillis += time;
        this._lastResponses.push({
            time: Date.now(),
            size: size
        });
        if (segment.rendition == null || !((_a = segment.rendition) === null || _a === void 0 ? void 0 : _a.isAudio)) {
            this.lastPreferedVideoChunkSize = size;
            this.lastPreferedVideoChunkDownloadTime = time;
        }
        if (segment.rendition != null && this.highestRenditionBandwidth < segment.rendition.bandwidth) {
            this.highestRenditionBandwidth = segment.rendition.bandwidth;
        }
        if (segment.rendition == null || !((_b = segment.rendition) === null || _b === void 0 ? void 0 : _b.isAudio)) {
            this.lastPreferedVideoBandwidth = segment.bandwidth;
            this.pushLastPreferedVideoBandwidth(segment.bandwidth);
        }
    }
    calculateEffectiveBadnwidth(chunkBitrate, chunkDuration) {
        let lastChunkDownloadTime = this.lastPreferedVideoChunkDownloadTime;
        let lastChunkSize = this.lastPreferedVideoChunkSize;
        let lastPingTime = this.lastPingTime;
        let correctedTime = lastChunkDownloadTime - lastPingTime;
        if (correctedTime <= 0) {
            lastPingTime = lastChunkDownloadTime;
            correctedTime = lastChunkDownloadTime;
        }
        if (lastChunkSize <= 0) {
            return 0;
        }
        let newChunkCalculatedSize = (chunkBitrate / 8) * chunkDuration;
        let newChunkCalculatedTime = (newChunkCalculatedSize * correctedTime) / lastChunkSize;
        let newChunkCalculatedCorrectedTime = newChunkCalculatedTime + (lastPingTime * 2); // AS a big ping time is a bad signal we weight 
        if (newChunkCalculatedCorrectedTime == 0) {
            return 0;
        }
        let newChunkCalculatedBandwidth = (newChunkCalculatedSize * 8) / (newChunkCalculatedCorrectedTime / 1000);
        console.log(this.name + " -> " + newChunkCalculatedBandwidth / 1000000);
        return newChunkCalculatedBandwidth;
    }
    getVideoBandwidth() {
        let bandwidth = ((this.downloadedBytesForScoring || 0) / ((this.downloadTimeForScoring || 1) / 1000)) * 8;
        return bandwidth;
    }
    /**
     * Returns the the expected bandwidth for the following request, based on previous requests.
     * @returns {number} Bandwidth value.
     * @public
     */
    getBandwidth() {
        return this._accumBw;
    }
    getAvgBandwidth() {
        let total = 0;
        let count = 0;
        for (const n of this.bandwidth.copyArray()) {
            total += n;
            count++;
        }
        return count > 0 ? total / count : 0;
    }
    getAvgRecentBandwidth() {
        let total = 0;
        let count = 0;
        for (const n of this.bandwidth.lastn(4)) {
            total += n;
            count++;
        }
        return count > 0 ? total / count : 0;
    }
    getAvgWBandwidth() {
        let total = 0;
        let count = 0;
        for (const n of this.wBandwidthForScoring.copyArray()) {
            total += n.bw;
            count += n.ct;
        }
        return count > 0 ? total / count : 0;
    }
    getAvgRecentWBandwidth() {
        let total = 0;
        let count = 0;
        for (const n of this.wBandwidthForScoring.lastn(4)) {
            total += n.bw;
            count += n.ct;
        }
        return count > 0 ? total / count : 0;
    }
    getWeightedBandwidth() {
        const a = this.getAvgWBandwidth();
        const b = this.getAvgRecentWBandwidth();
        return 0.45 * a + 0.55 * b;
    }
    /**
     * Resets the bandwidth value of the CDN, to discard previous wrong values and restart algorythms.
     * @public
     */
    resetBandwidth() {
        this._accumBw = Cdn.initialBw;
    }
    /**
     * Returns the timestamp of the oldest request stored in the lastResponses object, or 0 if is empty.
     * @returns {number} Timestamp of the oldest stored request.
     * @public
     */
    getOldestRequestTS() {
        var _a;
        return ((_a = this._lastResponses[0]) === null || _a === void 0 ? void 0 : _a.time) || 0;
    }
}
Cdn.initialBw = 9e12;
